import type { CompareBySelector, Primitive } from '@amzn/vinyl-util'

export function pickRandom<T>(array: readonly T[]): T | null {
    if (!array.length) return null
    const i = Math.floor(Math.random() * array.length)
    return array[i]
}

/**
 * Returns the item from the iterable that yields the highest score
 * based on the provided scoring function.
 *
 * @param items - An iterable collection of items
 * @param scoreFn - A function that computes a numeric score for each item. If undefined is returned, the element
 * will not be selected.
 * @returns The item with the highest score, or undefined if iterable is empty
 */
export function maxBy<T>(items: Iterable<T>, scoreFn: CompareBySelector<T>): T | null {
    let bestItem: T | null = null
    let bestScore: Exclude<Primitive, symbol> | undefined = undefined

    for (const item of items) {
        const score = scoreFn(item)
        if (score != null && (bestScore === undefined || score > bestScore)) {
            bestScore = score
            bestItem = item
        }
    }
    return bestItem
}

/**
 * Returns the item from the iterable that yields the lowest score
 * based on the provided scoring function.
 *
 * @param items - An iterable collection of items
 * @param scoreFn - A function that computes a numeric score for each item. If undefined is returned, the element
 * will not be selected.
 * @returns The item with the lowest score, or undefined if iterable is empty
 */
export function minBy<T>(items: Iterable<T>, scoreFn: CompareBySelector<T>): T | null {
    let bestItem: T | null = null
    let bestScore: Exclude<Primitive, symbol> | undefined = undefined

    for (const item of items) {
        const score = scoreFn(item)
        if (score != null && (bestScore === undefined || score < bestScore)) {
            bestScore = score
            bestItem = item
        }
    }
    return bestItem
}
